import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ArrowRight, Clock, Calendar, User, Share2, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import { formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = process.env.REACT_APP_BACKEND_URL;

const mdComponents = {
  h1: ({ node, ...p }) => <h1 className="text-3xl font-extrabold text-slate-900 mt-10 mb-4" {...p} />,
  h2: ({ node, ...p }) => <h2 className="text-2xl font-bold text-slate-900 mt-9 mb-3" {...p} />,
  h3: ({ node, ...p }) => <h3 className="text-xl font-bold text-slate-800 mt-7 mb-2" {...p} />,
  p: ({ node, ...p }) => <p className="text-slate-700 leading-relaxed my-4 text-[1.05rem]" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 my-4 space-y-2 text-slate-700" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 my-4 space-y-2 text-slate-700" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#FF6B00] font-semibold hover:underline" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-[#FF6B00] pl-5 italic text-slate-600 my-6 bg-orange-50/50 py-3 rounded-r-lg" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold text-slate-900" {...p} />,
  img: ({ node, ...p }) => <img className="rounded-xl my-6 w-full" {...p} alt={p.alt || ''} />,
  table: ({ node, ...p }) => <div className="overflow-x-auto my-6"><table className="w-full text-sm border border-slate-200 rounded-lg" {...p} /></div>,
  th: ({ node, ...p }) => <th className="bg-slate-50 border border-slate-200 px-3 py-2 text-left font-semibold" {...p} />,
  td: ({ node, ...p }) => <td className="border border-slate-200 px-3 py-2" {...p} />,
};

const ArtigoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/content/articles/${slug}`)
      .then(({ data }) => { setArticle(data.article); setRelated(data.related || []); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const onCtaClick = () => {
    axios.post(`${API}/content/articles/${slug}/cta-click`).catch(() => {});
  };

  const share = async () => {
    const url = `${SITE}/conteudos/${slug}`;
    if (navigator.share) { try { await navigator.share({ title: article.title, url }); } catch (e) {} }
    else { navigator.clipboard.writeText(url); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#FF6B00] border-t-transparent rounded-full animate-spin" /></div>;

  if (notFound || !article) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="artigo-not-found">
      <BookOpen className="w-14 h-14 text-slate-300 mb-4" />
      <h1 className="text-2xl font-bold text-slate-900">Artigo não encontrado</h1>
      <p className="text-slate-500 mt-2">Este conteúdo pode ter sido removido ou o endereço está incorreto.</p>
      <Link to="/conteudos" className="btn-hm mt-6">Ver todos os conteúdos</Link>
    </div>
  );

  const canonical = `${SITE}/conteudos/${slug}`;
  const pageTitle = `${article.meta_title || article.title} | HM Talent`;
  const coverAbs = article.cover_image_url
    ? (article.cover_image_url.startsWith('/') ? `${SITE}${article.cover_image_url}` : article.cover_image_url)
    : '';
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'Article',
    headline: article.title, description: article.meta_description || article.excerpt,
    image: coverAbs || undefined,
    datePublished: article.published_at, dateModified: article.updated_at,
    author: { '@type': 'Organization', name: article.author || 'HM Talent' },
    publisher: { '@type': 'Organization', name: 'HM Talent' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  };

  return (
    <div data-testid="artigo-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        {article.keywords?.length > 0 && <meta name="keywords" content={article.keywords.join(', ')} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        {coverAbs && <meta property="og:image" content={coverAbs} />}
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header */}
      <section className="hm-gradient text-white pt-14 pb-24 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="container-hm px-6 max-w-3xl mx-auto relative">
          <button onClick={() => navigate('/conteudos')} data-testid="artigo-back" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold mb-6">
            <ArrowLeft className="w-4 h-4" /> Voltar aos conteúdos
          </button>
          {article.category && <div className="text-sm font-bold uppercase tracking-wide text-white/90">{article.category}</div>}
          <h1 className="text-3xl md:text-5xl font-extrabold mt-3 leading-tight">{article.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/80">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {article.author || 'HM Talent'}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatDate(article.published_at)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {article.reading_minutes} min de leitura</span>
          </div>
        </div>
      </section>

      {/* Corpo */}
      <section className="bg-white -mt-16 relative pb-16">
        <div className="container-hm px-6 max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            {article.cover_image_url && (
              <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                <img src={article.cover_image_url} alt={article.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-7 md:p-10">
              {article.excerpt && <p className="text-lg text-slate-500 font-medium border-l-4 border-[#FF6B00] pl-4 mb-6">{article.excerpt}</p>}
              <article className="max-w-none" data-testid="artigo-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{article.body || ''}</ReactMarkdown>
              </article>

              {article.tags?.length > 0 && (
                <div className="mt-8 flex flex-wrap gap-2">
                  {article.tags.map((t) => <span key={t} className="text-xs font-semibold bg-slate-100 text-slate-600 px-3 py-1 rounded-full">#{t}</span>)}
                </div>
              )}

              {/* CTA */}
              {article.cta_label && article.cta_url && (
                <div className="mt-10 rounded-2xl hm-gradient text-white p-7 text-center" data-testid="artigo-cta">
                  <p className="text-lg font-bold">Gostou do conteúdo?</p>
                  {article.cta_url.startsWith('http') ? (
                    <a href={article.cta_url} target="_blank" rel="noopener noreferrer" onClick={onCtaClick}
                      className="inline-flex items-center gap-2 mt-4 bg-white text-[#EA5F00] font-bold px-7 py-3 rounded-full hover:bg-orange-50 transition-colors" data-testid="artigo-cta-btn">
                      {article.cta_label} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link to={article.cta_url} onClick={onCtaClick}
                      className="inline-flex items-center gap-2 mt-4 bg-white text-[#EA5F00] font-bold px-7 py-3 rounded-full hover:bg-orange-50 transition-colors" data-testid="artigo-cta-btn">
                      {article.cta_label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-400">{article.views} visualizações</span>
                <button onClick={share} data-testid="artigo-share" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#FF6B00]">
                  <Share2 className="w-4 h-4" /> Compartilhar
                </button>
              </div>
            </div>
          </div>

          {/* Relacionados */}
          {related.length > 0 && (
            <div className="mt-14">
              <h2 className="text-2xl font-extrabold text-slate-900 mb-6">Leia também</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((a, idx) => (
                  <Reveal key={a.id} variant="fadeUp" delay={idx * 80}>
                    <Link to={`/conteudos/${a.slug}`} data-testid={`related-${a.slug}`}
                      className="flex flex-col h-full rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl hover-lift group border border-slate-100">
                      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                        {a.cover_image_url
                          ? <img src={a.cover_image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          : <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center"><BookOpen className="w-8 h-8 text-orange-300" /></div>}
                      </div>
                      <div className="p-5">
                        <h3 className="font-bold text-slate-900 line-clamp-2 group-hover:text-[#FF6B00] transition-colors">{a.title}</h3>
                        <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.excerpt}</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ArtigoPage;
